// @ts-check
// Modal "Ativar fluxo de automação" (spec 2.0.0, fluxos.md seções 20.10 e 20.11; contrato 09 de fluxos, seção 17): a
// revisão por área (entrada, ações, envios e saída) com a situação, o resumo em linguagem simples e "Editar"; a entrada
// grande com a estimativa de envio e o "Entendi" obrigatório; os avisos de cards importados ou cadastrados à mão no
// WhatsApp de marketing e da escola sem consentimento pela entrada no funil. Linha com erro desabilita "Ativar fluxo".
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { Button } from '../../ui/button.js';
import { Checkbox } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { getEmailSettings } from '../../data/email-templates.js';
import { getWhatsappMessagingLimit } from '../../data/integrations.js';
import { formatNumber } from '../../lib/format.js';
import { describeEstimatedDays, describeEstimatedMinutes, estimateEmailMinutes, estimateWhatsappDays } from '../../lib/flow-estimates.js';
import { walkFlowNodes } from '../../../../shared/flows/flow-definition.js';
import { FlowDialog } from './flow-dialog.js';
import { FlowNotice } from './flow-ui.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/flows.js').FlowActivationPreview} FlowActivationPreview
 * @typedef {import('../../../../shared/flows/flow-definition.js').FlowDefinition} FlowDefinition
 * @typedef {'entry' | 'actions' | 'sends' | 'exit'} ActivationRow
 */

/**
 * Situação de uma linha: pronta, com aviso ou com erro.
 * @param {FlowActivationPreview} preview @param {ActivationRow} row
 * @returns {'READY' | 'WARNING' | 'ERROR'}
 */
export function activationRowStatus(preview, row) {
  const checklist = preview.checklist[row];
  if (!checklist?.isReady || Number(checklist?.problems ?? 0) > 0) return 'ERROR';
  const area = row === 'entry' ? 'ENTRY' : row === 'exit' ? 'EXIT' : 'NODE';
  const hasWarning = preview.problems.some((problem) => problem.severity === 'WARNING' && problem.area === area);
  return hasWarning ? 'WARNING' : 'READY';
}

/**
 * Frase da entrada no modal.
 * @param {FlowDefinition} definition @param {FlowActivationPreview} preview
 */
export function entrySummaryText(definition, preview) {
  if (definition.entry.mode === 'MANUAL_ONLY') return t('flows.activation.entryManual');
  if (definition.entry.mode === 'UNSET') return t('flows.activation.entryUnset');
  if (definition.entry.timing === 'EXISTING_AND_FUTURE' && preview.retroactive) {
    const key = preview.retroactive.isEstimate ? 'flows.activation.entryExistingEstimate' : 'flows.activation.entryExisting';
    return t(key, { count: preview.retroactive.count, number: formatNumber(preview.retroactive.count) });
  }
  return t('flows.activation.entryFuture');
}

/**
 * Frase dos envios: quantos e-mails e WhatsApps de cada finalidade e o lembrete do consentimento.
 * @param {FlowActivationPreview} preview
 */
export function sendsSummaryText(preview) {
  const { emails, whatsapp } = preview.sendEstimates;
  /** @type {string[]} */
  const parts = [];
  if (emails.marketing > 0) parts.push(t('flows.activation.emailsMarketing', { count: emails.marketing }));
  if (emails.service > 0) parts.push(t('flows.activation.emailsService', { count: emails.service }));
  if (whatsapp.marketing > 0) parts.push(t('flows.activation.whatsappMarketing', { count: whatsapp.marketing }));
  if (whatsapp.utility > 0) parts.push(t('flows.activation.whatsappUtility', { count: whatsapp.utility }));
  if (parts.length === 0) return t('flows.activation.noSends');
  const joined = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} ${t('flows.common.and')} ${parts[parts.length - 1]}`;
  const hasMarketing = emails.marketing + whatsapp.marketing > 0;
  return `${t('flows.activation.sendsLead', { sends: joined })}${hasMarketing ? ` ${t('flows.activation.consentReminder')}` : ''}`;
}

/**
 * Frase da saída: o fim e as outras condições ligadas.
 * @param {FlowDefinition} definition @param {(value: string) => string} stageLabel
 */
export function exitSummaryText(definition, stageLabel) {
  /** @type {string[]} */
  const parts = [t('flows.activation.exitParts.END')];
  for (const criterion of definition.exit.criteria) {
    if (criterion.kind === 'STAGE_REACHED') parts.push(t('flows.activation.exitParts.STAGE_REACHED', { stages: criterion.stages.map(stageLabel).join(', ') }));
    else parts.push(t(`flows.activation.exitParts.${criterion.kind}`));
  }
  const joined = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} ${t('flows.common.and')} ${parts[parts.length - 1]}`;
  return t('flows.activation.exitLead', { parts: joined });
}

/** Primeiro número de WhatsApp usado pelo fluxo (para o limite diário). @param {FlowDefinition} definition */
function firstWhatsappChannel(definition) {
  /** @type {string | null} */
  let channelId = null;
  walkFlowNodes(definition.nodes, (node) => {
    if (!channelId && node.type === 'SEND_WHATSAPP' && node.config?.channelId) channelId = node.config.channelId;
  });
  return channelId;
}

/**
 * @param {{ preview: FlowActivationPreview, definition: FlowDefinition, stageLabel: (value: string) => string, isBusy: boolean,
 *   onActivate: (acknowledgeLargeEntry: boolean) => void, onEdit: (row: ActivationRow) => void, onClose: () => void }} props
 */
export function FlowActivationModal({ preview, definition, stageLabel, isBusy, onActivate, onEdit, onClose }) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [rates, setRates] = useState(/** @type {{ perMinute: number | null, dailyLimit: number | null, used: number }} */ ({ perMinute: null, dailyLimit: null, used: 0 }));
  const isLarge = preview.retroactive?.warning === 'LARGE_ENTRY';
  const isTooLarge = preview.retroactive?.warning === 'TOO_LARGE';
  useEffect(() => {
    if (!isLarge) return;
    getEmailSettings().then((settings) => setRates((current) => ({ ...current, perMinute: /** @type {any} */ (settings)?.dispatchPerMinute ?? null }))).catch(() => undefined);
    const channelId = firstWhatsappChannel(definition);
    if (channelId) {
      getWhatsappMessagingLimit(channelId)
        .then((limit) => setRates((current) => ({ ...current, dailyLimit: limit?.portfolio?.limit ?? null, used: limit?.portfolio?.usedLast24h ?? 0 })))
        .catch(() => undefined);
    }
  }, [isLarge]);
  const rows = /** @type {Array<{ key: ActivationRow, title: string, text: string }>} */ ([
    { key: 'entry', title: t('flows.activation.rows.entry'), text: entrySummaryText(definition, preview) },
    {
      key: 'actions', title: t('flows.activation.rows.actions'),
      text: `${t('flows.activation.nodes', { count: Number(preview.checklist.actions.nodes ?? 0) })} ${definition.settings.considerWeekends ? t('flows.activation.weekendsOn') : t('flows.activation.weekendsOff')}`,
    },
    { key: 'sends', title: t('flows.activation.rows.sends'), text: sendsSummaryText(preview) },
    { key: 'exit', title: t('flows.activation.rows.exit'), text: exitSummaryText(definition, stageLabel) },
  ]);
  const statuses = rows.map((row) => activationRowStatus(preview, row.key));
  const hasErrors = statuses.includes('ERROR') || isTooLarge;
  const leads = preview.retroactive?.count ?? 0;
  const emailMinutes = estimateEmailMinutes({ leads, emailsPerLead: preview.sendEstimates.emails.firstStretch, perMinute: rates.perMinute });
  const whatsappDays = estimateWhatsappDays({ leads, messagesPerLead: preview.sendEstimates.whatsapp.firstStretch, dailyLimit: rates.dailyLimit, usedToday: rates.used });
  const emailEstimate = emailMinutes > 0 ? describeEstimatedMinutes(emailMinutes) : null;
  const whatsappEstimate = whatsappDays !== null && whatsappDays > 0 ? describeEstimatedDays(whatsappDays) : null;
  const imported = preview.warnings.find((warning) => warning.code === 'IMPORTED_OR_MANUAL_CARDS');
  const consentOff = preview.consentMode === 'OFF' && preview.sendEstimates.emails.marketing + preview.sendEstimates.whatsapp.marketing > 0;
  const icons = { READY: 'IconCircleCheck', WARNING: 'IconAlertTriangle', ERROR: 'IconAlertCircle' };

  return html`<${FlowDialog} title=${t('flows.activation.title')} subtitle=${t('flows.activation.subtitle')} size="medium" onClose=${onClose} testId="flow-activation-modal"
    footer=${html`<${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${onClose} />
      <${Button} title=${t('flows.activation.activate')} accent="blue" size="small" isLoading=${isBusy}
        disabled=${hasErrors || (isLarge && !acknowledged)} onClick=${() => onActivate(isLarge && acknowledged)} data-testid="flow-activate-confirm" />`}>
    <p class="flow-dialog-text">${t('flows.activation.text')}</p>
    <ul class="flow-activation-rows">
      ${rows.map((row, position) => html`<li key=${row.key} class="flow-activation-row" data-status=${statuses[position]}>
        <span class="flow-activation-status" aria-hidden="true"><${Icon} name=${icons[statuses[position]]} size=${20} /></span>
        <span class="flow-activation-texts">
          <span class="flow-activation-title">${row.title}<span class="visually-hidden-text">, ${t(`flows.activation.status.${statuses[position]}`)}</span></span>
          <span class="flow-activation-text">${row.text}</span>
        </span>
        <${Button} title=${t('flows.activation.edit')} variant="tertiary" size="small" onClick=${() => onEdit(row.key)}
          ariaLabel=${t('flows.activation.editRow', { row: row.title })} />
      </li>`)}
    </ul>
    ${isTooLarge ? html`<${FlowNotice} tone="warning" icon="IconAlertCircle">${t('flows.activation.tooLarge', { number: formatNumber(leads) })}<//>` : null}
    ${isLarge
      ? html`<div class="flow-activation-large" role="group" aria-label=${t('flows.activation.largeTitle')}>
          <p><strong>${t('flows.activation.largeLead', { number: formatNumber(leads) })}</strong></p>
          ${emailEstimate ? html`<p>${t('flows.activation.largeEmail', { duration: t(emailEstimate.key, { count: emailEstimate.count }) })}</p>` : null}
          ${whatsappEstimate
            ? html`<p>${t('flows.activation.largeWhatsapp', { limit: formatNumber(rates.dailyLimit ?? 0), duration: t(whatsappEstimate.key, { count: whatsappEstimate.count }) })}</p>`
            : preview.sendEstimates.whatsapp.firstStretch > 0 ? html`<p>${t('flows.activation.largeWhatsappUnknown')}</p>` : null}
          <label class="flow-checkbox-row">
            <${Checkbox} checked=${acknowledged} onChange=${(/** @type {boolean} */ value) => setAcknowledged(value)} ariaLabel=${t('flows.activation.acknowledge')} />
            <span>${t('flows.activation.acknowledge')}</span>
          </label>
        </div>`
      : null}
    ${imported
      ? html`<${FlowNotice} tone="warning" icon="IconAlertTriangle">${t('flows.activation.importedCards', {
          imported: formatNumber(Number(imported.details?.imported ?? 0)), manual: formatNumber(Number(imported.details?.manual ?? 0)),
        })}<//>`
      : null}
    ${consentOff ? html`<${FlowNotice} tone="warning" icon="IconShieldLock">${t('flows.problems.FUNNEL_CONSENT_OFF')}<//>` : null}
  <//>`;
}
